import { useContext, useRef } from 'react';
import { Modal, Form, Upload, Button } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import config from './Config';
import http from './Http';
import utils from './utils';
import { useTranslation } from 'react-i18next';

function LogoModifier({ showLogoModifier, logoPath, appIconPath, session, onClose }) {
    const { t } = useTranslation();
    const [form] = Form.useForm()
    const setTip = useContext(utils.tipContext)
    const refLogo = useRef()
    const refAppIcon = useRef()
    const uploadedLogoPath = useRef('')
    const uploadedAppIconPath = useRef('')

    const handleSubmit = async () => {
        const ps = await form.validateFields().catch(() => {return null})
        if (ps == null) {
            return
        }

        ps.DistributorID = session&&session.Distributors ? session&&session.Distributors&&session.Distributors[session.Distributors.length-1].ID : ''
        // If a new file was uploaded, use the captured path; otherwise reconstruct the current relative path
        ps.logoPath = uploadedLogoPath.current || (
            logoPath && logoPath.startsWith(config.apiUrl)
                ? './' + logoPath.substring(config.apiUrl.length)
                : (logoPath || '')
        );
        ps.appIcon = uploadedAppIconPath.current || (
            appIconPath && appIconPath.startsWith(config.apiUrl)
                ? './' + appIconPath.substring(config.apiUrl.length)
                : (appIconPath || '')
        );

        const resp = await http.post('logo/modify', ps).then(async r => {
            return await r.json()
        }).catch(e => {
            return null
        })

        try {
            if(resp.code != 200) {
                onClose()
                return
            }
            onClose({
                logoPath: resp.data.LogoPath,
                appIconPath: resp.data.AppIcon,
            })
        } catch(e) {
            onClose()
        }
    }

    const handleCancel = () => {
        onClose()
        form.resetFields()
    }

    return (
        <Modal
            title={t('Logo Modifier')}
            visible={showLogoModifier}
            cancelText={t('Cancel')}
            okText={t('Submit')}
            mask={true}
            maskClosable={false}
            width={600}
            onCancel={handleCancel}
            onOk={handleSubmit}
            footer={[
                <Button key="Cancel" onClick={handleCancel}>{t('Cancel')}</Button>,
                <Button key="Submit" onClick={handleSubmit}>{t('Submit')}</Button>,
            ]}
        >
            <Form 
                labelAlign="right"
                labelCol={{span: 7}}
                wrapperCol={{span: 16}}
                form={form}
            >
                <Form.Item
                    name="logoPath"
                    valuePropName="file"
                >
                    <Upload
                        maxCount={1}
                        accept=".png,.jpg,.jpeg"
                        name="logoPath"
                        action={config.apiUrl+"uploader/logo/do?Token="+http.token}
                        headers={{Token:http.token}}
                        listType="text"
                        beforeUpload={file => {
                            const fileType = file.type
                            const typeTrue = fileType==='image/png' || fileType==='image/jpg' || fileType==='image/jpeg'
                            if(!typeTrue) {
                                setTip({ 
                                    open: true, 
                                    severity: "error", 
                                    msg: t('The image type must be png,jpg,jpeg')
                                })
                                return Upload.LIST_IGNORE
                            }
                            return true
                        }}
                        defaultFileList={[{
                            status: "done",
                            url: logoPath ? logoPath : config.appIcon
                        }]}
                        onChange={r => {
                            if(r.file.status == 'done') {
                                const path = r.file.response.data[r.file.name];
                                refLogo.current.src = config.apiUrl + path;
                                uploadedLogoPath.current = path;
                            } else if (r.file.status === 'removed') {
                                uploadedLogoPath.current = '';
                            }
                        }}
                    >
                        <Button icon={<UploadOutlined />}>{t('Choose Logo')}</Button> 
                        <span style={{verticalAlign: 'bottom', fontStyle: 'italic', color: '#888'}}>
                            {t('the best logo size is 160px * 40px')}
                        </span>
                    </Upload>
                    <div style={{marginTop: '10px', height: '92px', backgroundColor: 'rgb(99, 173, 130)', paddingLeft: '24px', lineHeight: '92px'}}>
                        <img ref={refLogo} style={{width: '160px', height: '40px', verticalAlign: 'middle'}} 
                            src={logoPath ? logoPath : config.appIcon}
                        />
                    </div>
                </Form.Item>
                <Form.Item
                    name="appIcon"
                    valuePropName="file"
                    hidden={true}
                >
                    <Upload
                        maxCount={1}
                        accept=".png,.jpg,.jpeg,.ico"
                        name="appIcon"
                        action={config.apiUrl+"uploader/logo/do?Token="+http.token}
                        headers={{Token:http.token}}
                        listType="text"
                        beforeUpload={file => {
                            const fileType = file.type
                            const typeTrue = fileType==='image/png' || fileType==='image/jpg' || fileType==='image/jpeg' || fileType==='image/x-icon' || fileType==='image/vnd.microsoft.icon'
                            if(!typeTrue) {
                                setTip({ 
                                    open: true, 
                                    severity: "error", 
                                    msg: t('The image type must be png,jpg,jpeg,ico')
                                })
                                return Upload.LIST_IGNORE
                            }
                            return true
                        }}
                        defaultFileList={appIconPath ? [{
                            status: "done",
                            url: appIconPath
                        }] : []}
                        onChange={r => {
                            if(r.file.status == 'done') {
                                const path = r.file.response.data[r.file.name];
                                refAppIcon.current.src = config.apiUrl + path;
                                uploadedAppIconPath.current = path;
                            } else if (r.file.status === 'removed') {
                                uploadedAppIconPath.current = '';
                            }
                        }}
                    >
                        <Button icon={<UploadOutlined />}>{t('Choose App Icon')}</Button> 
                        <span style={{verticalAlign: 'bottom', fontStyle: 'italic', color: '#888'}}>
                            {t('recommended size 32px * 32px')}
                        </span>
                    </Upload>
                    <div style={{marginTop: '10px', backgroundColor: '#f5f5f5', padding: '12px 24px', display: 'flex', alignItems: 'center', gap: '12px'}}>
                        <span style={{fontSize: '13px', color: '#555'}}>{t('App Icon Preview')}:</span>
                        <img ref={refAppIcon} style={{width: '32px', height: '32px', verticalAlign: 'middle'}} 
                            src={appIconPath ? appIconPath : ''}
                        />
                    </div>
                </Form.Item>
            </Form>
        </Modal>
    )
}

export default LogoModifier;